import { z } from 'zod';
import { PaymentGatewayTerminalSaleStatus } from '../../../types/ipg/gateway-terminal-sale.types';
import {
    CardPointeLifecycleFinalizationGatewayOperation,
    CardPointeLifecycleFinalizationOutcome,
    CardPointeLifecycleFinalizationPayloadType,
    type CardPointeLifecycleFinalizationNormalizedResult,
    type CardPointeLifecycleFinalizationPayload,
    type CardPointeLifecycleFinalizationRequestDetails,
    type CardPointeLifecycleFinalizationRequestPayload,
    type CardPointeLifecycleFinalizationResponseDetails,
    type CardPointeLifecycleFinalizationResponsePayload,
} from '../../../types/ipg/cardpointe/cardpointe-lifecycle-finalization.types';
import { PaymentGatewayTerminalSaleResultSchema } from '../gateway-terminal-sale.schemas';
import { CardPointeTerminalSaleResponseDetailsSchema } from './cardpointe-terminal-sale-response.schemas';

export const CardPointeLifecycleFinalizationTerminalSaleStatusSchema: z.ZodType<
    CardPointeLifecycleFinalizationNormalizedResult['status']
> = z.enum([
    PaymentGatewayTerminalSaleStatus.APPROVED,
    PaymentGatewayTerminalSaleStatus.DECLINED,
    PaymentGatewayTerminalSaleStatus.CANCELED,
    PaymentGatewayTerminalSaleStatus.MALFORMED_RESPONSE,
]);

export const CardPointeLifecycleFinalizationRequestDetailsSchema: z.ZodType<CardPointeLifecycleFinalizationRequestDetails> =
    z.object({
        gateway_operation: z.literal(CardPointeLifecycleFinalizationGatewayOperation.TERMINAL_SALE),
        terminal_sale_response_details: CardPointeTerminalSaleResponseDetailsSchema,
        terminal_sale_result: PaymentGatewayTerminalSaleResultSchema,
    });

export const CardPointeLifecycleFinalizationNormalizedResultSchema: z.ZodType<CardPointeLifecycleFinalizationNormalizedResult> =
    z.object({
        gateway_operation: z.literal(CardPointeLifecycleFinalizationGatewayOperation.TERMINAL_SALE),
        status: CardPointeLifecycleFinalizationTerminalSaleStatusSchema,
        gateway_transaction_id: z.string().min(1).optional(),
        approved_amount: z.string().min(1).optional(),
        authorization_code: z.string().min(1).optional(),
        batch_id: z.string().min(1).optional(),
        masked_account: z.string().min(1).optional(),
        gateway_token: z.string().min(1).optional(),
        message: z.string().min(1).optional(),
        response_code: z.string().min(1).optional(),
    });

export const CardPointeLifecycleFinalizationFinalizedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeLifecycleFinalizationOutcome.FINALIZED>;
    normalized_result: typeof CardPointeLifecycleFinalizationNormalizedResultSchema;
}> = z.object({
    outcome: z.literal(CardPointeLifecycleFinalizationOutcome.FINALIZED),
    normalized_result: CardPointeLifecycleFinalizationNormalizedResultSchema,
}) satisfies z.ZodType<
    Extract<
        CardPointeLifecycleFinalizationResponseDetails,
        {
            readonly outcome: typeof CardPointeLifecycleFinalizationOutcome.FINALIZED;
        }
    >
>;

export const CardPointeLifecycleFinalizationMalformedResultResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CardPointeLifecycleFinalizationOutcome.MALFORMED_RESULT>;
    message: z.ZodString;
    body: z.ZodUnknown;
}> = z.object({
    outcome: z.literal(CardPointeLifecycleFinalizationOutcome.MALFORMED_RESULT),
    message: z.string().min(1),
    body: z.unknown(),
}) satisfies z.ZodType<
    Extract<
        CardPointeLifecycleFinalizationResponseDetails,
        {
            readonly outcome: typeof CardPointeLifecycleFinalizationOutcome.MALFORMED_RESULT;
        }
    >
>;

export const CardPointeLifecycleFinalizationResponseDetailsSchema: z.ZodType<CardPointeLifecycleFinalizationResponseDetails> =
    z.discriminatedUnion('outcome', [
        CardPointeLifecycleFinalizationFinalizedResponseDetailsSchema,
        CardPointeLifecycleFinalizationMalformedResultResponseDetailsSchema,
    ]);

export const CardPointeLifecycleFinalizationRequestPayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof CardPointeLifecycleFinalizationPayloadType.REQUEST
    >;
    request_details: typeof CardPointeLifecycleFinalizationRequestDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(CardPointeLifecycleFinalizationPayloadType.REQUEST),
    request_details: CardPointeLifecycleFinalizationRequestDetailsSchema,
}) satisfies z.ZodType<CardPointeLifecycleFinalizationRequestPayload>;

export const CardPointeLifecycleFinalizationResponsePayloadSchema: z.ZodObject<{
    gateway_lifecycle_payload_type: z.ZodLiteral<
        typeof CardPointeLifecycleFinalizationPayloadType.RESPONSE
    >;
    response_details: typeof CardPointeLifecycleFinalizationResponseDetailsSchema;
}> = z.object({
    gateway_lifecycle_payload_type: z.literal(CardPointeLifecycleFinalizationPayloadType.RESPONSE),
    response_details: CardPointeLifecycleFinalizationResponseDetailsSchema,
}) satisfies z.ZodType<CardPointeLifecycleFinalizationResponsePayload>;

export const CardPointeLifecycleFinalizationPayloadSchema: z.ZodType<CardPointeLifecycleFinalizationPayload> =
    z.discriminatedUnion('gateway_lifecycle_payload_type', [
        CardPointeLifecycleFinalizationRequestPayloadSchema,
        CardPointeLifecycleFinalizationResponsePayloadSchema,
    ]);
